/**
 * Variation Engine — draws unique DNA from category-weighted gene pools.
 *
 * Uses a seeded RNG (mulberry32) so a batch is reproducible: same seed +
 * same category + same history ⇒ same templates. `overrides` pins any gene
 * to a fixed value — this is the hook OpenAI will use to make design
 * decisions while the factory handles everything downstream.
 */

import { createDNA, OFFER_STYLES, FOOTER_STYLES, IMAGE_STYLES } from './designDNAEngine';
import { getCategory } from './categoryEngine';
import { getPaletteTheme } from './themeEngine';
import { getTypographyTheme } from './typographyThemeEngine';
import { getDecorationPack } from './decorationPackEngine';

/** Deterministic PRNG — small, fast, plenty for design variation. */
export const createRng = (seed = Date.now()) => {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];

export const IMAGE_COUNTS = [0, 1, 2];

/**
 * Generate one DNA record for a category.
 * `overrides` may pin any gene (layout, paletteTheme, seed, harmony, dark,
 * typographyTheme, typeScale, spacing, decorationPack, offerStyle,
 * footerStyle, imageCount, imageStyle). Derived genes (heroPosition,
 * ctaPosition, shapeStyle) cannot be overridden — they follow their source
 * gene by definition.
 */
export const generateDNA = (categoryId, rng = createRng(), overrides = {}) => {
  const cat = getCategory(categoryId);

  const layout = overrides.layout ?? pick(rng, cat.layouts);
  const paletteThemeId = overrides.paletteTheme ?? pick(rng, cat.paletteThemes);
  const paletteTheme = getPaletteTheme(paletteThemeId);
  const typographyThemeId = overrides.typographyTheme ?? pick(rng, cat.typographyThemes);
  const typographyTheme = getTypographyTheme(typographyThemeId);
  const decorationPackId = overrides.decorationPack ?? pick(rng, cat.decorationPacks);
  const decorationPack = getDecorationPack(decorationPackId);

  return createDNA({
    layout,
    paletteTheme: paletteThemeId,
    harmony: overrides.harmony ?? pick(rng, paletteTheme.harmonies),
    seed: overrides.seed ?? pick(rng, paletteTheme.seeds),
    dark: overrides.dark ?? rng() < paletteTheme.darkChance,
    typographyTheme: typographyThemeId,
    typeScale: overrides.typeScale ?? pick(rng, typographyTheme.scales),
    spacing: overrides.spacing ?? pick(rng, ['compact', 'comfortable', 'airy']),
    decorationPack: decorationPackId,
    shapeStyle: decorationPack.shapeStyle,
    offerStyle: overrides.offerStyle ?? pick(rng, OFFER_STYLES),
    footerStyle: overrides.footerStyle ?? pick(rng, FOOTER_STYLES),
    imageCount: overrides.imageCount ?? pick(rng, IMAGE_COUNTS),
    imageStyle: overrides.imageStyle ?? pick(rng, IMAGE_STYLES),
  });
};
